import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { useCache } from '../../context/CacheContext';
import { cachedPayerService, cachedPatientService } from '../../services/cachedApiService';
import { PriorAuthorization, Insurance, Patient } from '../../types';
import '../../styles/PendingAuthorizations.css';

const PendingAuthorizations: React.FC = () => {
  const [authorizations, setAuthorizations] = useState<PriorAuthorization[]>([]);
  const [filteredAuthorizations, setFilteredAuthorizations] = useState<PriorAuthorization[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [insurancePlans, setInsurancePlans] = useState<Record<number, Insurance>>({});
  const [patients, setPatients] = useState<Record<string, Patient>>({});
  const { user } = useAuth();
  const { invalidate } = useCache();

  useEffect(() => {
    const fetchData = async () => {
      if (!user || user.role !== 'payer') {
        setError('Only payers can access this page');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch pending authorizations for this payer
        const pendingAuths = await cachedPayerService.getPendingAuthorizations();
        
        // Fetch insurance plans to get patient information
        const plans = await cachedPayerService.getInsurancePlans();
        const plansMap: Record<number, Insurance> = {};
        plans.forEach(plan => {
          plansMap[plan.id] = plan;
        });
        
        // Fetch patient details for each authorization
        const patientIdsSet = new Set<string>();
        pendingAuths.forEach(auth => patientIdsSet.add(auth.patient));
        const patientIds = Array.from(patientIdsSet);
        const patientsMap: Record<string, Patient> = {};
        
        // Fetch patient details in parallel
        await Promise.all(
          patientIds.map(async (patientId) => {
            try {
              const patientDetail = await cachedPatientService.getPatientDetail(patientId);
              patientsMap[patientId] = patientDetail.patient;
            } catch (err) {
              console.error(`Error fetching details for patient ${patientId}:`, err);
              // Continue even if one patient fetch fails
            }
          })
        );
        
        setPatients(patientsMap);
        setInsurancePlans(plansMap);
        setAuthorizations(pendingAuths);
        setFilteredAuthorizations(pendingAuths);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching pending authorizations:', err);
        setError('Failed to fetch pending authorizations');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user]);

  const handleReview = (authId: number) => {
    // Navigate to review page
    window.location.href = `/payer/review-authorization/${authId}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading pending authorizations...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error-container">
          <div className="message-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0378 2.66667 10.268 4L3.33978 16C2.56998 17.3333 3.53223 19 5.07183 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="message-text">{error}</div>
          <div className="message-actions" style={{ marginTop: '15px' }}>
            <button
              onClick={() => {
                invalidate.priorAuths();
                invalidate.patients();
                window.location.reload();
              }}
              className="retry-button"
              style={{
                padding: '8px 16px',
                backgroundColor: '#3182ce',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pending-auth-container">
        <h2 className="pending-auth-title">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M14 3V7C14 7.55 14.45 8 15 8H19" stroke="currentColor" strokeWidth="2" />
            <path d="M17 21H7C5.90 21 5 20.10 5 19V5C5 3.89 5.90 3 7 3H14L19 8V19C19 20.10 18.10 21 17 21Z" stroke="currentColor" strokeWidth="2" />
            <path d="M12 12H12.01M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Pending Authorizations
        </h2>

        {/* Search Bar */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="search-icon">
              <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search by patient name, ID, type, or urgency..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                if (value.trim() === '') {
                  setFilteredAuthorizations(authorizations);
                } else {
                  setFilteredAuthorizations(
                    authorizations.filter(auth => {
                      const patient = patients[auth.patient];
                      const patientName = patient ? `${patient.fname} ${patient.lname}`.toLowerCase() : '';
                      
                      return auth.patient.toLowerCase().includes(value.toLowerCase()) ||
                        auth.request_type.toLowerCase().includes(value.toLowerCase()) ||
                        auth.urgency.toLowerCase().includes(value.toLowerCase()) ||
                        (auth.clinical_reason && auth.clinical_reason.toLowerCase().includes(value.toLowerCase())) ||
                        patientName.includes(value.toLowerCase());
                    })
                  );
                }
              }}
              className="search-input"
            />
            {searchTerm && (
              <button
                className="search-clear-btn"
                onClick={() => {
                  setSearchTerm('');
                  setFilteredAuthorizations(authorizations);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
          <div className="pending-count">
            {filteredAuthorizations.length} {filteredAuthorizations.length === 1 ? 'authorization' : 'authorizations'} pending review
          </div>
        </div>

        <ul className="pending-list">
          {filteredAuthorizations.length > 0 ? (
            filteredAuthorizations.map((auth) => (
              <li
                key={auth.id}
                className={`pending-item ${auth.urgency === 'urgent' ? 'urgent' : ''}`}
              >
                <div className="pending-item-left">
                  <div className={`pending-avatar ${auth.request_type}`}>
                    {auth.request_type === 'prescription' ? 'Rx' : 'Pr'}
                  </div>
                  <div className="pending-info">
                    <div className="pending-patient-name">
                      {patients[auth.patient] ?
                        `${patients[auth.patient].fname} ${patients[auth.patient].lname}` :
                        `Patient ID: ${auth.patient}`}
                    </div>
                    <div className="pending-meta">
                      <span className="pending-id">ID: {auth.patient}</span>
                      <span>•</span>
                      <span className="pending-type">
                        {auth.request_type.charAt(0).toUpperCase() + auth.request_type.slice(1)}
                      </span>
                      <span>•</span>
                      <span className="pending-date">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {auth.created_at?.split(' ')[0].slice(0,10)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pending-item-right">
                  <span className={`badge ${auth.urgency === 'urgent' ? 'warning' : 'normal'}`}>
                    {auth.urgency === 'urgent' ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M12 9V13M12 17H12.01M5.1 19H18.9C20.5 19 21.4 17.33 20.7 16L13.7 4C13 2.67 11 2.67 10.3 4L3.3 16C2.6 17.33 3.5 19 5.1 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                    {auth.urgency.charAt(0).toUpperCase() + auth.urgency.slice(1)}
                  </span>
                  
                  <span className="reason-badge" title={auth.clinical_reason || 'No clinical reason provided'}>
                    {auth.clinical_reason || 'No clinical reason provided'}
                  </span>

                  <button
                    onClick={() => handleReview(auth.id)}
                    className="review-btn"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2.45825 12C3.73253 7.94288 7.52281 5 12.0004 5C16.4781 5 20.2684 7.94291 21.5426 12C20.2684 16.0571 16.4781 19 12.0005 19C7.52281 19 3.73251 16.0571 2.45825 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Review
                  </button>
                </div>
              </li>
            ))
          ) : (
            <li className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path d="M14 3V7C14 7.55 14.45 8 15 8H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M17 21H7C5.90 21 5 20.10 5 19V5C5 3.89 5.90 3 7 3H14L19 8V19C19 20.10 18.10 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 12H12.01M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div className="empty-state-title">
                {authorizations.length > 0
                  ? `No authorizations found matching "${searchTerm}"`
                  : "No pending authorizations found"}
              </div>
              <div className="empty-state-subtitle">
                {authorizations.length > 0
                  ? "Try a different search term or clear the search"
                  : "All authorizations have been reviewed"}
              </div>
              {authorizations.length > 0 && searchTerm && (
                <button
                  className="clear-search-btn"
                  onClick={() => {
                    setSearchTerm('');
                    setFilteredAuthorizations(authorizations);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
                  </svg>
                  Clear search
                </button>
              )}
            </li>
          )}
        </ul>
      </div>
    </Layout>
  );
};

export default PendingAuthorizations;